import React, { useState, useEffect } from "react";
import RecipeInputForm from "./RecipeInputForm";
import RecipeResult from "./RecipeResult";
import ImageResult from "./ImageResult";
import Loading from "./Loading";
import CulinaryLoadingModal from "./CulinaryLoadingModal";
import RecipeHistoryComponent from "./RecipeHistoryComponent";
import { fetchRecipeGemini } from "../service/APIService";
import { v4 as uuid } from "uuid";
import secureLocalStorage from "react-secure-storage";
import { useNavigate } from "react-router-dom";
import { useSubscription } from "../context/SubscriptionContext";

const Home = () => {
  const { refreshRecipeCount, handleOpenExternal } = useSubscription();
  const [activeTab, setActiveTab] = useState("ingredients"); // "ingredients" or "name"
  const [recipe, setRecipe] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [image, setImage] = useState(null);
  const [loggedInUser, setLoggedInUser] = useState(null);
  const [lastParams, setLastParams] = useState(null);
  const navigate = useNavigate();
  const loggedInUserEmail = secureLocalStorage.getItem("loggedInUserEmail");

  useEffect(() => {
    const sessionUser = JSON.parse(secureLocalStorage.getItem("loggedInUser"));
    setLoggedInUser(sessionUser);
  }, [loading]);

  const handleFetchRecipe = async (ingredients, cuisine, preference, action = "full", recipeNameInput = "") => {
    setLastParams({ ingredients, cuisine, preference, action, recipeNameInput });
    setLoading(true);
    setError("");
    try {
      const email = loggedInUser?.email || "guest";

      if (action === "recipe_by_name") {
        // Step 1: Generate Recipe Text (extremely fast)
        const textResult = await fetchRecipeGemini("", cuisine, email, 'recipe_by_name_text', recipeNameInput, "", preference);
        if (textResult.recipe) {
          setRecipe({ candidates: [{ content: { parts: [{ text: textResult.recipe }] } }] });
        } else {
          throw new Error("Failed to generate recipe.");
        }

        // Extract Recipe Name for Image Step (using generated text for better accuracy)
        const recipeText = textResult.recipe;
        const nameMatch = recipeText.match(/~(.*?)~/);
        const recipeName = nameMatch ? nameMatch[1] : recipeNameInput;

        // Step 2: Generate Image & Save to DB (happens in background)
        const imageResult = await fetchRecipeGemini(`Search: ${recipeNameInput}`, cuisine, email, 'image_and_save', recipeName, recipeText, preference);
        if (imageResult.image_url) {
          setImage(imageResult.image_url);
        }
      } else {
        // Step 1: Generate Recipe Text (extremely fast)
        const textResult = await fetchRecipeGemini(ingredients, cuisine, email, 'text_only', "", "", preference);
        if (textResult.recipe) {
          setRecipe({ candidates: [{ content: { parts: [{ text: textResult.recipe }] } }] });
        } else {
          throw new Error("Failed to generate recipe.");
        }

        // Extract Recipe Name for Image Step
        const recipeText = textResult.recipe;
        const nameMatch = recipeText.match(/~(.*?)~/);
        const recipeName = nameMatch ? nameMatch[1] : (cuisine + " Dish");

        // Step 2: Generate Image & Save to DB (happens seamlessly while they read the recipe!)
        const imageResult = await fetchRecipeGemini(ingredients, cuisine, email, 'image_and_save', recipeName, recipeText, preference);
        if (imageResult.image_url) {
          setImage(imageResult.image_url);
        }
      }
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    const isGuest = loggedInUserEmail === "guest";
    if (isGuest || window.confirm("Are you sure you want to log out from all apps?")) {
      secureLocalStorage.removeItem("loggedInUser");
      secureLocalStorage.removeItem("guestUser");
      secureLocalStorage.setItem("isLoggedOut", true);
      navigate("/");
    }
  };

  return (
    <div className="container py-2 mb-5 pb-5">
      {/* Top Hero Header matching Reference Mockup (media_1793017968504.png) */}
      <div className="mb-3 pt-1">
        <div>
          <h2
            className="fw-bolder text-dark mb-0"
            style={{
              fontSize: "clamp(1.25rem, 5.5vw, 1.65rem)",
              lineHeight: 1.2,
              letterSpacing: "-0.4px",
              whiteSpace: "nowrap"
            }}
          >
            What are you <span style={{ color: "#ff6347" }}>cooking</span> today?
          </h2>
          <p className="text-secondary small mb-0 mt-1" style={{ fontSize: "12.5px" }}>
            Turn ingredients in your fridge into delicious meals
          </p>
        </div>
      </div>

      {/* 2-Way Mode Chips matching Reference Theme */}
      <div className="d-flex align-items-center gap-2 mb-3 flex-wrap">
        <button
          type="button"
          className={`mode-pill-chip ${activeTab === "ingredients" ? "active" : ""}`}
          onClick={() => setActiveTab("ingredients")}
        >
          <i className="fi fi-rr-list-check" style={{ fontSize: "0.95rem" }}></i>
          <span>By Fridge Ingredients</span>
        </button>

        <button
          type="button"
          className={`mode-pill-chip ${activeTab === "name" ? "active" : ""}`}
          onClick={() => setActiveTab("name")}
        >
          <i className="fi fi-rr-restaurant" style={{ fontSize: "0.95rem" }}></i>
          <span>By Dish Name</span>
        </button>
      </div>

      {/* Responsive Foldable / Tablet Landscape Container */}
      <div className="home-screen-layout">
        <div className="home-left-col">
          <div className="mb-4">
            <RecipeInputForm
              onFetchRecipe={handleFetchRecipe}
              loading={loading}
              activeTab={activeTab}
              setActiveTab={setActiveTab}
            />
          </div>

          {error && (
            <div className="alert alert-danger d-flex justify-content-between align-items-center shadow-sm">
              <div className="flex-grow-1 mb-0">
                <i className="fi fi-rr-info me-2"></i>
                {error}
              </div>
              <button 
                className="btn btn-sm btn-outline-danger ms-3 text-nowrap" 
                onClick={() => handleFetchRecipe(lastParams.ingredients, lastParams.cuisine, lastParams.preference, lastParams.action, lastParams.recipeNameInput)}
              >
                <i className="fi fi-rr-refresh me-1"></i> Retry
              </button>
            </div>
          )}

          {loading && (
            <>
              <CulinaryLoadingModal isOpen={loading} onClose={() => setLoading(false)} />
              <div className="py-2">
                <Loading />
              </div>
            </>
          )}

          {!loading && (image || recipe) && (
            <div className="mb-4">
              <ImageResult image={image} />
              <RecipeResult recipe={recipe} />
            </div>
          )}
        </div>

        <div className="home-right-col">
          {!loading && (
            <div id="recipe-history-section">
              <RecipeHistoryComponent showLatest={true} />
            </div>
          )}
        </div>
      </div>
      <div className="text-center mt-3">
        <a
          href="https://www.apple.com/legal/internet-services/itunes/dev/stdeula/"
          className="text-muted text-decoration-underline"
          onClick={(e) => {
            e.preventDefault();
            handleOpenExternal("https://www.apple.com/legal/internet-services/itunes/dev/stdeula/");
          }}
          target="_blank"
          rel="noopener noreferrer"
        >
          <small>Terms of Use (EULA)</small>
        </a>
      </div>
    </div>
  );
};

export default Home;
